import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import moment from 'moment';
import cx from 'classnames';
import { isEmpty } from 'lodash';
import { BarLoader } from '@unite-us/ui';
import { findInsuranceByDateRange, requestDateOutOfRange } from 'src/common/utils/Dates/utils';
import useZcodes from 'src/common/hooks/useZcodes';
import IconButton from 'src/common/display/Profile/components/IconButton';
import { useCurrentProviderId, useIsNetworkLead } from 'src/common/contexts/CurrentProviderContext';
import { hasInvoicesAccess } from 'src/common/utils/FeatureFlags/flags';
import {
  useFind,
  usePopulate,
  useFindRecord,
} from 'src/api/APIHooks';
import { useFindMostRecentInvoice, useUpdateProvidedService } from 'src/components/Cases/hooks';
import buildProcedureCodeInitialFormValues from 'src/components/Cases/utils/buildProcedureCodeInitialFormValues';
import './CardData.scss';
import InvoiceStatus from '../InvoiceStatus';

const CardHeader = ({
  className,
  isBillable,
  metafields,
  onEdit,
  providedService,
  programName,
  showInteractions,
  ignoreSocialCareExpiredDates,
  canInvoiceAboveRemainingAuthorizedAmount,
  feeScheduleProgram,
  serviceAuthorization,
  serviceAuthorizationInvoiceInformation,
  personId,
  hasInvoicesRole,
}) => {
  const providerId = useCurrentProviderId();
  const isNetworkLead = useIsNetworkLead();

  const [submitting, setSubmitting] = useState(false);
  const isEditingDisabled = providedService.state !== 'active';
  const { updateProvidedService, isReady: isUpdateReady } = useUpdateProvidedService(providedService.id);
  const onSubmit = () => updateProvidedService({
    state: 'submitted',
  });

  const metadata = providedService.metadata || [];

  const requiredMetaDataMissing = metafields.some(({ field, validations }) => {
    if (validations && validations.required) {
      const metaDataMatch = metadata.find((md) => md.field === field);

      if (!metaDataMatch) return true;

      const value = metaDataMatch.value || {};

      const metaDataMatchEmpty = field === 'address_field' ? (
        !value.line_1 || !value.city || !value.state || !value.postal_code
      ) : isEmpty(metaDataMatch.value);

      return metaDataMatchEmpty;
    }

    return false;
  });

  const { invoice: mostRecentInvoice, isLoading: isInvoiceLoading } = useFindMostRecentInvoice(providedService.id);

  const { data: chosenPlan } = useFindRecord(
    'plan',
    providedService?.plan?.id,
    { queryConfig: { enabled: !!providedService?.plan?.id, placeholderData: undefined } },
  );
  const casePayerId = chosenPlan?.data?.data?.payer?.id;

  const { data: insurancesData } = useFind('insurance', {
    plan: providedService?.plan?.id,
    person: personId,
    state: 'active,pending,inactive',
    groupId: providerId,
  }, { queryConfig: { placeholderData: undefined, enabled: !!providedService } });

  const insurances = insurancesData?.data?.data;

  const isZcodeAssociationForced = feeScheduleProgram?.force_zcode_associations;
  const { data: zcodes } = useZcodes(feeScheduleProgram?.id, casePayerId, isZcodeAssociationForced);
  const zcodeOptions = isZcodeAssociationForced ? [] :
    zcodes;
  const shouldShowZcodeSelect = !isZcodeAssociationForced && !isEmpty(zcodeOptions);
  const zCodesNonValid = shouldShowZcodeSelect && isEmpty(providedService?.zcodes);

  const insuranceRequired = feeScheduleProgram?.insurance_required;
  const startDate = moment(providedService?.starts_at);
  const endDate = providedService?.ends_at ? moment(providedService?.ends_at) : startDate;
  let validInsurance;
  if (startDate) {
    validInsurance = findInsuranceByDateRange({
      insurances,
      startDate,
      endDate: endDate || startDate,
      ignoreSocialCareExpiredDates,
      serviceAuthorization,
    });
  }

  const requestedAmountNonValid = !canInvoiceAboveRemainingAuthorizedAmount &&
    (serviceAuthorizationInvoiceInformation?.remainingAuthorizedAmount < 0 ||
      providedService?.unit_amount > serviceAuthorizationInvoiceInformation?.remainingAuthorizedAmount);

  const serviceAuthorizationStartDate = moment(serviceAuthorization?.approved_starts_at);
  const serviceAuthorizationEndDate =
    serviceAuthorization?.approved_ends_at ?
      moment(serviceAuthorization?.approved_ends_at) : serviceAuthorizationStartDate;

  const requestedDatesNonValid = serviceAuthorization && (
    requestDateOutOfRange(startDate, serviceAuthorizationStartDate, serviceAuthorizationEndDate) ||
    requestDateOutOfRange(endDate, serviceAuthorizationStartDate, serviceAuthorizationEndDate)
  );

  const placeOfServiceOptions = feeScheduleProgram?.place_of_services;
  const shouldShowPlaceOfServiceSelect = !isEmpty(placeOfServiceOptions);
  const placeOfServiceNonValid = shouldShowPlaceOfServiceSelect && !providedService?.place_of_service;

  usePopulate(
    'procedure_code_modifiers',
    'procedure_code_modifiers',
    providedService,
    { queryConfig: { placeholderData: undefined, enabled: !!providedService } },
  );

  const procedureCodes = buildProcedureCodeInitialFormValues({ providedService, feeScheduleProgram });

  const nonValidProcedureCodes = procedureCodes.some(
    (code) => !code.procedure_code || isEmpty(code.procedure_code_modifiers),
  );

  const isSubmitDisabled = requiredMetaDataMissing ||
    !providedService?.unit_amount ||
    !providedService?.starts_at ||
    (insuranceRequired && !validInsurance) ||
    requestedAmountNonValid ||
    zCodesNonValid ||
    placeOfServiceNonValid ||
    requestedDatesNonValid ||
    nonValidProcedureCodes ||
    !isUpdateReady;

  const showSubmitButton = showInteractions && isBillable && providedService.state === 'active';

  const handleSubmit = async () => {
    setSubmitting(true);
    await onSubmit();
    setSubmitting(false);
  };

  return (
    <div className={className}>
      <div className="flex justify-between space-x-4">
        <h3 className="flex-grow text-lg text-text-blue leading-snug">
          {programName} Provided
        </h3>
        {showInteractions && (
          <div className="flex flex-shrink items-start space-x-6 mt-1">
            <IconButton
              className={cx(!showSubmitButton && 'hidden')}
              disabled={isSubmitDisabled || submitting}
              icon="Submit"
              id="submit-provided-service"
              labelText="Submit"
              onClick={() => handleSubmit()}
            />
            <IconButton
              disabled={isEditingDisabled}
              icon="Pencil"
              labelText="Edit"
              id="edit-provided-service"
              onClick={onEdit}
            />
          </div>
        )}
      </div>
      {isBillable && (
        <div className="mt-2 flex space-x-2">
          <span className="text-dark-grey whitespace-nowrap">
            {moment(providedService.updated_at).utc().format('LL')}
          </span>
          {!!mostRecentInvoice?.short_id && !isNetworkLead && (
            hasInvoicesRole ? (
              <Link
                className="whitespace-nowrap pl-8"
                to={`/invoices/${mostRecentInvoice.id}`}
              >
                {`Invoice #${mostRecentInvoice.short_id}`}
              </Link>
            ) : (
              <span className="whitespace-nowrap pl-8">
                {`Invoice #${mostRecentInvoice.short_id}`}
              </span>
            )
          )}
          {isInvoiceLoading ? (
            <BarLoader />
          ) : (
            <InvoiceStatus invoice={mostRecentInvoice} />
          )}
        </div>
      )}
    </div>
  );
};

CardHeader.propTypes = {
  className: PropTypes.string,
  isBillable: PropTypes.bool.isRequired,
  metafields: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  programName: PropTypes.string.isRequired,
  providedService: PropTypes.object.isRequired,
  showInteractions: PropTypes.bool.isRequired,
  ignoreSocialCareExpiredDates: PropTypes.bool.isRequired,
  canInvoiceAboveRemainingAuthorizedAmount: PropTypes.bool.isRequired,
  feeScheduleProgram: PropTypes.object.isRequired,
  serviceAuthorization: PropTypes.object.isRequired,
  serviceAuthorizationInvoiceInformation: PropTypes.object.isRequired,
  personId: PropTypes.string.isRequired,
  hasInvoicesRole: PropTypes.bool.isRequired,
};

CardHeader.defaultProps = {
  className: '',
};

const mapStateToProps = (state) => ({
  hasInvoicesRole: hasInvoicesAccess(state),
});

export default connect(mapStateToProps)(CardHeader);
